import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { PageHeader } from "@/components/patterns";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

export interface PaymentTerm {
  id: string;
  name: string;
  advance_pct: number | string;
  net_days: number;
  is_default: boolean;
  status: boolean;
  label: string;
}

const th = "h-[34px] border-b border-border px-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const td = "border-b border-border/60 px-2.5 py-2";

/** The ways customers may pay, chosen on a customer, a quote or an order */
export function PaymentTermsPage() {
  const { success, error } = useToast();
  const [terms, setTerms] = useState<PaymentTerm[] | null>(null);
  const [editing, setEditing] = useState<PaymentTerm | "new" | null>(null);
  const [form, setForm] = useState({ name: "", advance_pct: "0", net_days: "0", is_default: false, status: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setTerms(unwrapArray<PaymentTerm>(await api.get("/api/PaymentTerm/List?all=true")));
    } catch (e: any) {
      error("Could not load", e?.message || "Please try again");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (t: PaymentTerm | "new") => {
    setEditing(t);
    setForm(t === "new" ? { name: "", advance_pct: "0", net_days: "30", is_default: false, status: true } : { name: t.name, advance_pct: String(Number(t.advance_pct)), net_days: String(t.net_days), is_default: t.is_default, status: t.status });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return error("Name needed", "Give the term a name, e.g. Net 30");
    const body = { name: form.name.trim(), advance_pct: Number(form.advance_pct) || 0, net_days: Math.round(Number(form.net_days) || 0), is_default: form.is_default, status: form.status };
    setBusy(true);
    try {
      if (editing && editing !== "new") await api.put(`/api/PaymentTerm/Update/${editing.id}`, body);
      else await api.post("/api/PaymentTerm/Insert", body);
      success("Payment term saved", body.name);
      setEditing(null);
      await load();
    } catch (err: any) {
      error("Could not save", err?.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: PaymentTerm) => {
    if (!window.confirm(`Remove "${t.name}"? If customers, quotes or orders use it, it is switched off instead.`)) return;
    try {
      const r: any = await api.delete(`/api/PaymentTerm/Delete/${t.id}`);
      const d = r?.AddtionalData ?? r?.data ?? r;
      success(d?.switched_off ? "Switched off" : "Removed", d?.switched_off ? `${t.name} is in use, so it is hidden from new choices` : t.name);
      await load();
    } catch (err: any) {
      error("Could not remove", err?.message || "Please try again");
    }
  };

  const previewLabel = () => {
    const adv = Number(form.advance_pct) || 0;
    const days = Math.round(Number(form.net_days) || 0);
    const rest = days > 0 ? `net ${days} days` : "on delivery";
    if (adv >= 100) return "100% advance";
    if (adv > 0) return `${adv}% advance, balance ${rest}`;
    return days > 0 ? `Net ${days} days` : "Payment on delivery";
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payment terms"
        actions={
          <Button size="sm" onClick={() => open("new")}>
            <Plus className="size-4 mr-1.5" /> Add term
          </Button>
        }
      />
      <p className="text-[13px] text-muted-foreground">How customers pay. Each customer can have a usual term; quotes and orders start with it and keep the wording they were made with.</p>

      {!terms ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <div className="overflow-x-auto rounded-[10px] border border-border bg-card">
          <table className="w-full min-w-[520px] border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-muted/40">
                <th className={th}>Name</th>
                <th className={th}>Reads as</th>
                <th className={`${th} w-[90px]`}>Advance</th>
                <th className={`${th} w-[90px]`}>Net days</th>
                <th className={`${th} w-[80px]`}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {terms.map((t) => (
                <tr key={t.id} className={t.status ? "" : "opacity-60"}>
                  <td className={`${td} font-medium`}>
                    {t.name} {t.is_default && <Badge variant="blue" className="ml-1.5">Default</Badge>} {!t.status && <Badge variant="outline" className="ml-1.5">Off</Badge>}
                  </td>
                  <td className={td}>{t.label}</td>
                  <td className={td}>{Number(t.advance_pct)}%</td>
                  <td className={td}>{t.net_days}</td>
                  <td className={td}>
                    <span className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon-sm" aria-label={`Edit ${t.name}`} onClick={() => open(t)}><Pencil className="size-3.5" /></Button>
                      {!t.is_default && <Button variant="ghost" size="icon-sm" aria-label={`Remove ${t.name}`} onClick={() => remove(t)}><Trash2 className="size-3.5 text-danger" /></Button>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add payment term" : "Edit payment term"}</DialogTitle>
            <DialogDescription>Reads as: <strong>{previewLabel()}</strong></DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} placeholder="e.g. 30% advance, balance net 45" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Advance %</Label><Input type="number" min={0} max={100} value={form.advance_pct} onChange={(e) => setForm({ ...form, advance_pct: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Balance due after (days)</Label><Input type="number" min={0} max={365} value={form.net_days} onChange={(e) => setForm({ ...form, net_days: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Use when a customer has none</label>
            {editing !== "new" && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.status} onChange={(e) => setForm({ ...form, status: e.target.checked })} /> Available to choose</label>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
